import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  ForwardedRef,
  useImperativeHandle,
} from "react";

interface MaskCoordinates {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Position {
  x: number;
  y: number;
  offsetX?: number;
  offsetY?: number;
  initialScale?: Scale;
  initialPosition?: Position;
  width?: number;
  height?: number;
  initialRotation?: number;
  centerX?: number;
  centerY?: number;
}

interface Scale {
  x: number;
  y: number;
}

interface Point {
  x: number;
  y: number;
}

interface TransformHandles {
  topLeft: Point;
  topRight: Point;
  bottomLeft: Point;
  bottomRight: Point;
  midTop: Point;
  midRight: Point;
  midBottom: Point;
  midLeft: Point;
  topRotate: Point;
  center: Point;
}

interface HandleResult {
  type: "move" | "resize" | "rotate";
  name?: string;
}

// Define the transform data that will be saved
export interface ProductPosition {
  scale: Scale;
  position: Position;
  rotation: number;
}

interface PhotoEditorProps {
  baseImageUrl?: string;
  overlayImageUrl?: string;
  readOnly?: boolean;
  initialMaskCoordinates?: MaskCoordinates | null;
  initialTransform?: ProductPosition;
  onTransformChange?: (transform: ProductPosition) => void;
  onCoverageChange?: (isCovered: boolean) => void;
}

// Helper function to safely get transform coordinates
const getOverlayDimensions = (
  overlayImage: HTMLImageElement | null,
  scale: Scale
) => {
  const width = overlayImage?.width ? overlayImage.width * scale.x : 0;
  const height = overlayImage?.height ? overlayImage.height * scale.y : 0;
  return { width, height };
};

const PhotoEditor = forwardRef(
  (
    {
      baseImageUrl,
      overlayImageUrl,
      readOnly = false,
      initialMaskCoordinates = null,
      initialTransform,
      onTransformChange,
      onCoverageChange,
    }: PhotoEditorProps,
    ref: ForwardedRef<any>
  ) => {
    const [baseImage, setBaseImage] = useState<HTMLImageElement | null>(null);
    const [overlayImage, setOverlayImage] = useState<HTMLImageElement | null>(
      null
    );
    const [finalImage, setFinalImage] = useState<HTMLImageElement | null>(null);
    const [productFillImage, setProductFillImage] = useState<HTMLImageElement | null>(null);
    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const [isRotating, setIsRotating] = useState<boolean>(false);
    const [resizeHandle, setResizeHandle] = useState<string>("");
    const [startPosition, setStartPosition] = useState<Position>({
      x: 0,
      y: 0,
    });
    const [scale, setScale] = useState<Scale>({ x: 1, y: 1 });
    const [rotation, setRotation] = useState<number>(0);
    const [selected, setSelected] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const transformControlSize = 20; // Size of transform controls (rotation and resize handles)
    const transformChangeTimeoutRef = useRef<number | null>(null);
    const transformRef = useRef<ProductPosition | null>(null);
    // Store the original mask coordinates separately to display the blue bounding box
    const originalMaskCoordinatesRef = useRef<MaskCoordinates | null>(null);
    // Track the coverage status to avoid unnecessary re-renders
    const lastCoverageResult = useRef<boolean>(true);
    const coverageCheckTimeoutRef = useRef<number | null>(null);

    // Add state to track DOM dimensions for overlay positioning
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
    const [canvasScale, setCanvasScale] = useState({ x: 1, y: 1 });
    const [canvasPosition, setCanvasPosition] = useState({ x: 0, y: 0 });

    // Expose methods to parent components via ref
    useImperativeHandle(ref, () => ({
      generateProductMask: (
        actorId: string,
        projectId: string,
        apiService: any
      ) => generateProductMask(actorId, projectId, apiService),
      generateProductFillImage: (
        actorId: string,
        projectId: string,
        apiService: any
      ) => generateProductFillImage(actorId, projectId, apiService),
      getDefaultTransform: (): ProductPosition => {
        return {
          position: { ...position },
          scale: { ...scale },
          rotation: rotation,
        };
      },
    }));

    // Stable initialization of transform values
    const initializationRef = useRef({
      initialized: false,
      hasAppliedInitialTransform: false
    });

    // Apply initialTransform if provided, with stabilization to prevent glitches
    useEffect(() => {
      // Only proceed if we have the overlay image loaded
      if (!overlayImage) return;

      // Prevent double initialization which can cause glitches
      // This effect is now only a fallback - most initialization happens in the image loading effect
      if (initializationRef.current.initialized && initializationRef.current.hasAppliedInitialTransform) {
        return;
      }

      // Store original mask coordinates for blue bounding box if available
      if (initialMaskCoordinates) {
        originalMaskCoordinatesRef.current = { ...initialMaskCoordinates };
      }

      // Skip actual transform application since it's now done in the image loading effect
      // This prevents the "jump" that was happening

      return () => {
        // Clean up timeout on unmount
        if (coverageCheckTimeoutRef.current) {
          window.clearTimeout(coverageCheckTimeoutRef.current);
        }
      };
    }, [initialTransform, initialMaskCoordinates, overlayImage]);

    // Efficient coverage check function that won't cause re-renders
    const checkSelectionCoverage = () => {
      if (
        !overlayImage ||
        !originalMaskCoordinatesRef.current ||
        !onCoverageChange
      ) {
        return true;
      }

      const { x, y, width, height } = originalMaskCoordinatesRef.current;

      // Get the transform handles (which include padding and represent the purple outline)
      const handles = getTransformHandles();
      if (
        !handles.topLeft ||
        !handles.topRight ||
        !handles.bottomLeft ||
        !handles.bottomRight
      ) {
        return true; // If handles aren't available, assume it's covered
      }

      // Get the coordinates of the purple selection outline (including padding)
      const purpleOutlinePoints = [
        handles.topLeft,
        handles.topRight,
        handles.bottomRight,
        handles.bottomLeft,
      ];

      // Find min and max coordinates to create the bounding box of the purple outline
      const minX = Math.min(...purpleOutlinePoints.map((p) => p.x));
      const maxX = Math.max(...purpleOutlinePoints.map((p) => p.x));
      const minY = Math.min(...purpleOutlinePoints.map((p) => p.y));
      const maxY = Math.max(...purpleOutlinePoints.map((p) => p.y));

      // Create the corners of the blue outline
      const blueOutlineCorners = [
        { x, y }, // top-left
        { x: x + width, y }, // top-right
        { x: x + width, y: y + height }, // bottom-right
        { x, y: y + height }, // bottom-left
      ];

      // Check if the blue outline is completely inside the purple outline bounding box
      const isCovered = blueOutlineCorners.every(
        (point) =>
          point.x >= minX &&
          point.x <= maxX &&
          point.y >= minY &&
          point.y <= maxY
      );

      // Only notify if the coverage status has changed
      if (isCovered !== lastCoverageResult.current) {
        lastCoverageResult.current = isCovered;
        onCoverageChange(isCovered);
      }

      return isCovered;
    };

    // Calculate the coordinates of the transform handles
    const getTransformHandles = (): Partial<TransformHandles> => {
      if (!overlayImage) return {};

      // Add padding to the selection box (in pixels)
      // Increased padding for easier handle interaction
      const paddingX = 16; // Increased horizontal padding for better usability
      const paddingTopY = 16; // Increased top padding for better usability
      const paddingBottomY = 24; // Increased bottom padding for better usability

      const { width, height } = getOverlayDimensions(overlayImage, scale);

      // Center point of the overlay
      const centerX = position.x + width / 2;
      const centerY = position.y + height / 2;

      // Calculate rotated corner points
      const cos = Math.cos((rotation * Math.PI) / 180);
      const sin = Math.sin((rotation * Math.PI) / 180);

      const rotatePoint = (
        x: number,
        y: number,
        cx: number,
        cy: number
      ): Point => {
        const dx = x - cx;
        const dy = y - cy;
        return {
          x: cx + dx * cos - dy * sin,
          y: cy + dx * sin + dy * cos,
        };
      };

      // Points before rotation (with padding)
      const topLeft = { x: position.x - paddingX, y: position.y - paddingTopY };
      const topRight = {
        x: position.x + width + paddingX,
        y: position.y - paddingTopY,
      };
      const bottomLeft = {
        x: position.x - paddingX,
        y: position.y + height + paddingBottomY,
      };
      const bottomRight = {
        x: position.x + width + paddingX,
        y: position.y + height + paddingBottomY,
      };
      const midTop = {
        x: position.x + width / 2,
        y: position.y - paddingTopY,
      };
      const midRight = {
        x: position.x + width + paddingX,
        y: position.y + height / 2,
      };
      const midBottom = {
        x: position.x + width / 2,
        y: position.y + height + paddingBottomY,
      };
      const midLeft = {
        x: position.x - paddingX,
        y: position.y + height / 2,
      };

      // Rotate points
      const rotatedTopLeft = rotatePoint(
        topLeft.x,
        topLeft.y,
        centerX,
        centerY
      );
      const rotatedTopRight = rotatePoint(
        topRight.x,
        topRight.y,
        centerX,
        centerY
      );
      const rotatedBottomLeft = rotatePoint(
        bottomLeft.x,
        bottomLeft.y,
        centerX,
        centerY
      );
      const rotatedBottomRight = rotatePoint(
        bottomRight.x,
        bottomRight.y,
        centerX,
        centerY
      );
      const rotatedMidTop = rotatePoint(midTop.x, midTop.y, centerX, centerY);
      const rotatedMidRight = rotatePoint(
        midRight.x,
        midRight.y,
        centerX,
        centerY
      );
      const rotatedMidBottom = rotatePoint(
        midBottom.x,
        midBottom.y,
        centerX,
        centerY
      );
      const rotatedMidLeft = rotatePoint(
        midLeft.x,
        midLeft.y,
        centerX,
        centerY
      );

      // Single rotation handle at the top
      const rotationOffset = 24; // Offset for rotation handle position
      const topRotate = rotatePoint(
        midTop.x,
        midTop.y - rotationOffset,
        centerX,
        centerY
      );

      return {
        topLeft: rotatedTopLeft,
        topRight: rotatedTopRight,
        bottomLeft: rotatedBottomLeft,
        bottomRight: rotatedBottomRight,
        midTop: rotatedMidTop,
        midRight: rotatedMidRight,
        midBottom: rotatedMidBottom,
        midLeft: rotatedMidLeft,
        topRotate,
        center: { x: centerX, y: centerY },
      };
    };

    // Show loading indicator while images are loading
    const toggleLoadingIndicator = (show: boolean) => {
      const fallbackLoader = document.getElementById("image-loading-fallback");
      if (fallbackLoader) {
        fallbackLoader.style.opacity = show ? "1" : "0";
        fallbackLoader.style.pointerEvents = show ? "auto" : "none";
      }
    };

    useEffect(() => {
      // Show loading indicator initially
      setIsLoading(true);
      toggleLoadingIndicator(true);

      // Hide loading indicator when both images are loaded
      if (baseImage && (!overlayImageUrl || overlayImage)) {
        setIsLoading(false);
        toggleLoadingIndicator(false);
      }
    }, [baseImage, overlayImage, overlayImageUrl]);

    useEffect(() => {
      if (baseImageUrl) {
        toggleLoadingIndicator(true);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          setBaseImage(img);
          setFinalImage(null);
          if (!overlayImageUrl) {
            setIsLoading(false);
            toggleLoadingIndicator(false);
          }
        };
        img.onerror = (e) => {
          console.error("Error loading base image:", e);
          setIsLoading(false);
          toggleLoadingIndicator(false);
        };
        const proxyUrl = baseImageUrl?.replace(
          "https://pub-a1df6f0fac0048fa854b3e1a7db8e6d2.r2.dev",
          "https://img.ugz.ai"
        );
        img.src = proxyUrl;
      }
    }, [baseImageUrl]);

    // Ref to prevent initial flash
    const initialRenderRef = useRef(true);

    // Load overlay image from URL if provided, with stabilized initialization
    useEffect(() => {
      if (overlayImageUrl && baseImage) {
        toggleLoadingIndicator(true);

        // Prevent unnecessary rerenders during loading
        if (isLoading) {
          const img = new Image();
          img.crossOrigin = "anonymous";

          // Preload the image but don't display it yet
          img.onload = () => {
            // Calculate the initial transform before showing the image
            // This prevents the full-screen flash

            // If initialTransform is provided, use those values for pre-calculation
            let preScaleX = 1;
            let preScaleY = 1;
            let prePositionX = 0;
            let prePositionY = 0;
            let preRotation = 0;

            if (initialTransform) {
              // Use initialTransform values for pre-calculation
              preScaleX = initialTransform.scale.x;
              preScaleY = initialTransform.scale.y;
              prePositionX = initialTransform.position.x;
              prePositionY = initialTransform.position.y;
              preRotation = initialTransform.rotation;
            } else if (initialMaskCoordinates) {
              // If no initialTransform, calculate from mask coordinates
              const { x, y, width } = initialMaskCoordinates;
              // Calculate scale to fit within mask width
              preScaleX = width / img.width;
              preScaleY = preScaleX; // Maintain aspect ratio
              prePositionX = x;
              prePositionY = y;
            }

            // Bundle all operations into a single animation frame to prevent flashing
            requestAnimationFrame(() => {
              // Apply all transformations before rendering the image
              setScale({
                x: preScaleX,
                y: preScaleY,
              });

              setPosition({
                x: prePositionX,
                y: prePositionY,
              });

              setRotation(preRotation);

              // Add a delay before showing the image to ensure transforms are applied first
              // This prevents the brief full-screen flash
              setTimeout(() => {
                // Only after setting the transform values, display the image
                setOverlayImage(img);

                // Mark initialization as complete to prevent re-application
                // Setting these flags inside the timeout ensures the image doesn't show
                // until transforms are properly applied
                initializationRef.current.initialized = true;
                initializationRef.current.hasAppliedInitialTransform = true;

                // Mark as loaded and selected - do this inside the timeout
                // to ensure all state changes happen in the same batch
                setFinalImage(null);
                setSelected(true);
                setIsLoading(false);
                toggleLoadingIndicator(false);

                // Check coverage after a delay - moved inside timeout
                // to ensure all operations happen in the correct sequence
                if (coverageCheckTimeoutRef.current) {
                  window.clearTimeout(coverageCheckTimeoutRef.current);
                }
                coverageCheckTimeoutRef.current = window.setTimeout(() => {
                  checkSelectionCoverage();
                }, 300);
              }, 50);
            });
          };

          img.onerror = (e) => {
            console.error("Error loading overlay image:", e);
            setIsLoading(false);
            toggleLoadingIndicator(false);
          };

          // Apply proxy URL if needed
          const proxyUrl = overlayImageUrl?.replace(
            "https://pub-a1df6f0fac0048fa854b3e1a7db8e6d2.r2.dev",
            "https://img.ugz.ai"
          );

          // Start loading the image
          img.src = proxyUrl;
        }
      }
    }, [overlayImageUrl, baseImage, isLoading, initialTransform, initialMaskCoordinates]);

    // Check if mouse is over a transform handle
    const getHandleAtPosition = (x: number, y: number): HandleResult | null => {
      if (!overlayImage) return null;

      const handles = getTransformHandles();
      if (!handles.center) return null;

      const handlePoints = [
        { name: "topLeft", point: handles.topLeft },
        { name: "topRight", point: handles.topRight },
        { name: "bottomLeft", point: handles.bottomLeft },
        { name: "bottomRight", point: handles.bottomRight },
      ];

      // Replace multiple rotation handles with a single top rotation handle
      const rotationHandle = { name: "topRotate", point: handles.topRotate };

      // Check resize handles
      for (const handle of handlePoints) {
        if (
          handle.point &&
          x >= handle.point.x - transformControlSize / 2 &&
          x <= handle.point.x + transformControlSize / 2 &&
          y >= handle.point.y - transformControlSize / 2 &&
          y <= handle.point.y + transformControlSize / 2
        ) {
          return { type: "resize", name: handle.name };
        }
      }

      // Check rotation handle - using circular hit area instead of square
      // This makes the entire circle area clickable, not just the center
      if (rotationHandle.point) {
        const handleX = rotationHandle.point.x;
        const handleY = rotationHandle.point.y;
        const hitRadius = transformControlSize / 2; // Hit area matches handle size
        
        // Calculate distance from mouse to handle center (using Pythagorean theorem)
        const distance = Math.sqrt(
          Math.pow(x - handleX, 2) + Math.pow(y - handleY, 2)
        );
        
        // If distance is less than hit radius, we're inside the circle
        if (distance <= hitRadius) {
          return { type: "rotate", name: rotationHandle.name };
        }
      }

      // Check if within the overlay image
      const { width, height } = getOverlayDimensions(overlayImage, scale);
      const center = handles.center;

      // Transform point back to check if inside the rectangle
      const cos = Math.cos((-rotation * Math.PI) / 180); // Negative rotation to transform back
      const sin = Math.sin((-rotation * Math.PI) / 180);
      const centerX = center.x;
      const centerY = center.y;

      const dx = x - centerX;
      const dy = y - centerY;
      const transformedX = centerX + dx * cos - dy * sin;
      const transformedY = centerY + dx * sin + dy * cos;

      if (
        transformedX >= position.x &&
        transformedX <= position.x + width &&
        transformedY >= position.y &&
        transformedY <= position.y + height
      ) {
        return { type: "move" };
      }

      return null;
    };

    // Mouse events for interactions
    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!overlayImage || finalImage) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) * (canvas.width / rect.width);
      const y = (e.clientY - rect.top) * (canvas.height / rect.height);

      const handle = getHandleAtPosition(x, y);

      if (handle) {
        setSelected(true);

        if (handle.type === "move") {
          setIsDragging(true);

          // For move operations, we need to track starting point and current position
          // Store the current position to prevent state inconsistencies
          setStartPosition({
            x: x,
            y: y,
            initialPosition: { ...position } // Store the current position to use as a base
          });
        } else if (
          handle.type === "resize" &&
          (handle.name === "topLeft" ||
            handle.name === "topRight" ||
            handle.name === "bottomLeft" ||
            handle.name === "bottomRight")
        ) {
          setIsResizing(true);
          setResizeHandle(handle.name);
          const handles = getTransformHandles();
          const center = handles.center;

          if (!center) return;

          setStartPosition({
            x,
            y,
            initialScale: { ...scale },
            initialPosition: { ...position },
            width: overlayImage.width * scale.x,
            height: overlayImage.height * scale.y,
            initialRotation: rotation,
            centerX: center.x,
            centerY: center.y,
          });
        } else if (handle.type === "rotate") {
          setIsRotating(true);
          const handles = getTransformHandles();
          const center = handles.center;

          if (!center) return;

          // Change cursor to grabbing when rotating
          canvas.style.cursor = "grabbing";

          // For rotation, we need to store the center point and initial rotation
          setStartPosition({
            x,
            y,
            initialRotation: rotation,
            centerX: center.x,
            centerY: center.y,
          });
        }
      } else {
        setSelected(false);
      }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (finalImage) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      // We only use this for cursor updates now since transformations are handled in global mousemove
      if (!isDragging && !isResizing && !isRotating) {
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) * (canvas.width / rect.width);
        const y = (e.clientY - rect.top) * (canvas.height / rect.height);

        // Update cursor based on what's under the mouse
        const handle = getHandleAtPosition(x, y);

        if (handle) {
          if (handle.type === "move") {
            canvas.style.cursor = "move";
          } else if (handle.type === "resize") {
            switch (handle.name) {
              case "topLeft":
              case "bottomRight":
                canvas.style.cursor = "nwse-resize";
                break;
              case "topRight":
              case "bottomLeft":
                canvas.style.cursor = "nesw-resize";
                break;
              default:
                canvas.style.cursor = "not-allowed";
                break;
            }
          } else if (handle.type === "rotate") {
            canvas.style.cursor = "grab"; // Use standard grab cursor for better compatibility
          }
        } else {
          canvas.style.cursor = "default";
        }
      }
    };

    const handleMouseUp = () => {
      // Local mouse up handler - but we'll primarily use the global one
      // Just reset cursor state
      if (isRotating) {
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.style.cursor = "grab";
        }
      }

      // State changes are now handled by the global handler
      // This avoids duplicate processing
    };

    // Flag to track if first render with transforms is complete
    const hasRenderedWithTransformsRef = useRef(false);

    // Render only the final image on canvas (for mask generation and download)
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;

      if (baseImage) {
        // Set canvas dimensions to match base image
        canvas.width = baseImage.width;
        canvas.height = baseImage.height;

        // Only draw on canvas when using final image
        if (finalImage) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(finalImage, 0, 0);

          // Also draw border around the final image
          ctx.strokeStyle = "#9ca3af"; // Gray-400
          ctx.lineWidth = 1;
          ctx.strokeRect(0, 0, canvas.width, canvas.height);
        }
      }
    }, [
      baseImage,
      finalImage
    ]);

    // When transform changes (position, scale, rotation), debounce the coverage check
    useEffect(() => {
      if (!overlayImage || !originalMaskCoordinatesRef.current) return;

      // Clear any existing timeout
      if (coverageCheckTimeoutRef.current) {
        window.clearTimeout(coverageCheckTimeoutRef.current);
      }

      // Set a new timeout to check coverage after user stops making changes
      coverageCheckTimeoutRef.current = window.setTimeout(() => {
        checkSelectionCoverage();
      }, 300); // 300ms debounce

      return () => {
        if (coverageCheckTimeoutRef.current) {
          window.clearTimeout(coverageCheckTimeoutRef.current);
        }
      };
    }, [position.x, position.y, scale.x, scale.y, rotation]);

    // Simplified mask generation
    const handleMergeLayers = () => {
      if (!baseImage || !overlayImage) return;

      // Create a new canvas for the mask
      const maskCanvas = document.createElement("canvas");
      maskCanvas.width = baseImage.width;
      maskCanvas.height = baseImage.height;
      const ctx = maskCanvas.getContext("2d");

      if (!ctx) return;

      // Fill with black background
      ctx.fillStyle = "black";
      ctx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);

      // Draw white shape for the selected area
      ctx.fillStyle = "white";
      ctx.save();

      // Get the center and dimensions of the overlay
      const center = {
        x: position.x + (overlayImage.width * scale.x) / 2,
        y: position.y + (overlayImage.height * scale.y) / 2,
      };

      // Apply the same transformations as the overlay
      ctx.translate(center.x, center.y);
      ctx.rotate((rotation * Math.PI) / 180);

      // Draw a white rectangle representing the overlay area
      ctx.fillRect(
        (-overlayImage.width * scale.x) / 2,
        (-overlayImage.height * scale.y) / 2,
        overlayImage.width * scale.x,
        overlayImage.height * scale.y
      );

      ctx.restore();

      // Show the mask in the canvas
      const dataURL = maskCanvas.toDataURL("image/png");
      const img = new Image();
      img.onload = () => {
        setFinalImage(img);
        setSelected(false);
      };
      img.src = dataURL;
    };

    // Generate and upload mask for video generation
    const generateProductMask = async (
      actorId: string,
      projectId: string,
      apiService: any
    ) => {
      if (!baseImage || !overlayImage) {
        console.error("Cannot generate mask: missing base or overlay image");
        return null;
      }

      // Create a new canvas for the mask
      const maskCanvas = document.createElement("canvas");
      maskCanvas.width = baseImage.width;
      maskCanvas.height = baseImage.height;
      const ctx = maskCanvas.getContext("2d");

      if (!ctx) {
        console.error("Cannot get canvas context");
        return null;
      }

      // Fill with black background
      ctx.fillStyle = "black";
      ctx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);

      // Draw white shape for the selected area
      ctx.fillStyle = "white";
      ctx.save();

      // Get the center and dimensions of the overlay
      const center = {
        x: position.x + (overlayImage.width * scale.x) / 2,
        y: position.y + (overlayImage.height * scale.y) / 2,
      };

      // Apply the same transformations as the overlay
      ctx.translate(center.x, center.y);
      ctx.rotate((rotation * Math.PI) / 180);

      // Draw a white rectangle representing the overlay area
      ctx.fillRect(
        (-overlayImage.width * scale.x) / 2,
        (-overlayImage.height * scale.y) / 2,
        overlayImage.width * scale.x,
        overlayImage.height * scale.y
      );

      ctx.restore();

      try {
        // Convert canvas to blob
        const blob = await new Promise<Blob>((resolve, reject) => {
          maskCanvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error("Failed to create blob from canvas"));
          }, "image/png");
        });

        // Create a file from the blob
        const filename = `product_mask_${actorId}.png`;
        const file = new File([blob], filename, { type: "image/png" });

        // Create form data for upload
        const formData = new FormData();
        formData.append("file", file);
        formData.append("project_id", projectId);
        formData.append("actor_id", actorId);
        formData.append("is_product_mask", "true");

        // Upload using the provided API service
        const response = await apiService.post(
          "/api/files/upload-file",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        console.log("Mask uploaded successfully:", response.data);
        return response.data;
      } catch (error) {
        console.error("Error generating or uploading mask:", error);
        return null;
      }
    };

    // Generate and upload product fill image for video generation
    const generateProductFillImage = async (
      actorId: string,
      projectId: string,
      apiService: any
    ) => {
      if (!baseImage || !overlayImage) {
        console.error("Cannot generate fill image: missing base or overlay image");
        return null;
      }

      // Create a new canvas for the fill image
      const fillCanvas = document.createElement("canvas");
      fillCanvas.width = baseImage.width;
      fillCanvas.height = baseImage.height;
      const ctx = fillCanvas.getContext("2d");

      if (!ctx) {
        console.error("Cannot get canvas context");
        return null;
      }

      // Draw only the overlay image in the correct position
      ctx.save();

      // Get the center and dimensions of the overlay
      const center = {
        x: position.x + (overlayImage.width * scale.x) / 2,
        y: position.y + (overlayImage.height * scale.y) / 2,
      };

      // Apply the same transformations as the overlay
      ctx.translate(center.x, center.y);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(scale.x, scale.y);

      // Draw the overlay image
      ctx.drawImage(
        overlayImage,
        -overlayImage.width / 2,
        -overlayImage.height / 2
      );

      ctx.restore();

      try {
        // Convert canvas to blob
        const blob = await new Promise<Blob>((resolve, reject) => {
          fillCanvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error("Failed to create blob from canvas"));
          }, "image/png");
        });

        // Create a file from the blob
        const filename = `product_fill_${actorId}.png`;
        const file = new File([blob], filename, { type: "image/png" });

        // Save to state for preview if needed
        const dataURL = fillCanvas.toDataURL("image/png");
        const img = new Image();
        img.onload = () => {
          setProductFillImage(img);
        };
        img.src = dataURL;

        // Create form data for upload
        const formData = new FormData();
        formData.append("file", file);
        formData.append("project_id", projectId);
        formData.append("actor_id", actorId);
        formData.append("is_product_fill", "true");

        // Upload using the provided API service
        const response = await apiService.post(
          "/api/files/upload-file",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        console.log("Fill image uploaded successfully:", response.data);
        return response.data;
      } catch (error) {
        console.error("Error generating or uploading fill image:", error);
        return null;
      }
    };

    // Store drag state in a ref to avoid state synchronization issues
    const dragStateRef = useRef({
      isDragging: false,
      startX: 0,
      startY: 0,
      initialPositionX: 0,
      initialPositionY: 0
    });

    // Update the ref when drag state changes
    useEffect(() => {
      if (isDragging && startPosition.initialPosition) {
        dragStateRef.current = {
          isDragging: true,
          startX: startPosition.x,
          startY: startPosition.y,
          initialPositionX: startPosition.initialPosition.x,
          initialPositionY: startPosition.initialPosition.y
        };
      } else if (!isDragging) {
        dragStateRef.current.isDragging = false;
      }
    }, [isDragging, startPosition]);

    // Add global mouse event handlers to track dragging outside canvas
    useEffect(() => {
      // Using requestAnimationFrame for smoother dragging
      let animationFrameId: number | null = null;

      // Create a style element for temporary global styles during drag operations
      const styleElement = document.createElement('style');
      styleElement.id = 'photo-editor-drag-style';

      const handleGlobalMouseMove = (e: MouseEvent) => {
        const container = containerRef.current;
        if (!container || !overlayImage) return;

        // Add global text selection prevention during drag operations
        if ((isDragging || isResizing || isRotating) && !document.getElementById('photo-editor-drag-style')) {
          styleElement.textContent = `
            * {
              -webkit-user-select: none !important;
              -moz-user-select: none !important;
              -ms-user-select: none !important;
              user-select: none !important;
              cursor: ${isDragging ? 'grabbing' : isResizing ? 'nwse-resize' : 'grabbing'} !important;
            }
          `;
          document.head.appendChild(styleElement);
        }

        // Cancel any pending animation frame
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
        }

        // Schedule position update in animation frame for smoother performance
        animationFrameId = requestAnimationFrame(() => {
          // Get container position relative to window
          const rect = container.getBoundingClientRect();

          // Get mouse position relative to container
          const mouseX = e.clientX - rect.left;
          const mouseY = e.clientY - rect.top;

          // Convert to canvas coordinates
          const canvasCoords = convertPositionToCanvas(mouseX, mouseY);

          // Handle dragging
          if (dragStateRef.current.isDragging) {
            // Calculate delta from the fixed reference point
            const dx = canvasCoords.x - dragStateRef.current.startX;
            const dy = canvasCoords.y - dragStateRef.current.startY;

            // Apply movement with the fixed initial position as base
            setPosition({
              x: dragStateRef.current.initialPositionX + dx,
              y: dragStateRef.current.initialPositionY + dy
            });
          }
          // Handle resizing
          else if (isResizing && startPosition.initialScale) {
            // Calculate how much the mouse has moved
            const deltaY = canvasCoords.y - startPosition.y;

            // Initial width and height
            const initialWidth = startPosition.width ||
                               overlayImage.width * startPosition.initialScale.x;
            const initialHeight = startPosition.height ||
                                overlayImage.height * startPosition.initialScale.y;

            // Different scaling based on which handle was dragged
            let scaleFactor = 1;
            if (resizeHandle === "bottomRight" || resizeHandle === "bottomLeft") {
              scaleFactor = 1 + deltaY / initialHeight;
            } else if (resizeHandle === "topRight" || resizeHandle === "topLeft") {
              scaleFactor = 1 - deltaY / initialHeight;
            }

            // Ensure minimum scale
            scaleFactor = Math.max(0.1, scaleFactor);

            // Calculate new dimensions while maintaining aspect ratio
            let newWidth = initialWidth * scaleFactor;
            let newHeight = initialHeight * scaleFactor;

            // Ensure minimum size
            newWidth = Math.max(20, newWidth);
            newHeight = Math.max(20, newHeight);

            // Calculate new scale
            const newScaleX = newWidth / overlayImage.width;
            const newScaleY = newHeight / overlayImage.height;

            // Calculate new position to maintain center
            const centerX = startPosition.centerX;
            const centerY = startPosition.centerY;
            if (centerX !== undefined && centerY !== undefined) {
              const newX = centerX - newWidth / 2;
              const newY = centerY - newHeight / 2;
              setPosition({ x: newX, y: newY });
            }

            setScale({ x: newScaleX, y: newScaleY });
          }
          // Handle rotation
          else if (isRotating) {
            if (startPosition.initialRotation !== undefined &&
                startPosition.centerX !== undefined &&
                startPosition.centerY !== undefined) {

              // Calculate angle between center and current mouse position
              const dx = canvasCoords.x - startPosition.centerX;
              const dy = canvasCoords.y - startPosition.centerY;
              const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

              // Calculate angle between center and initial mouse position
              const initialDx = startPosition.x - startPosition.centerX;
              const initialDy = startPosition.y - startPosition.centerY;
              const initialAngle = (Math.atan2(initialDy, initialDx) * 180) / Math.PI;

              // Calculate rotation delta and add to initial rotation
              let newRotation = startPosition.initialRotation + (angle - initialAngle);

              // Normalize rotation to 0-360
              newRotation = ((newRotation % 360) + 360) % 360;

              // Snapping to angles disabled per requirement

              setRotation(newRotation);
            }
          }

          animationFrameId = null;
        });
      };

      const handleGlobalMouseUp = (e: MouseEvent) => {
        // Cancel any pending animation frame
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
          animationFrameId = null;
        }

        if (isDragging || isResizing || isRotating) {
          // End interactions
          setIsDragging(false);
          setIsResizing(false);
          setIsRotating(false);

          // Remove global selection prevention style when done dragging
          const existingStyle = document.getElementById('photo-editor-drag-style');
          if (existingStyle && existingStyle.parentNode) {
            existingStyle.parentNode.removeChild(existingStyle);
          }

          // Update parent with transform data
          if (onTransformChange && overlayImage) {
            const currentTransform: ProductPosition = {
              position: {
                x: position.x,
                y: position.y,
              },
              scale: {
                x: scale.x,
                y: scale.y,
              },
              rotation,
            };
            onTransformChange(currentTransform);

            // Check coverage with debounce
            if (coverageCheckTimeoutRef.current) {
              window.clearTimeout(coverageCheckTimeoutRef.current);
            }
            coverageCheckTimeoutRef.current = window.setTimeout(() => {
              checkSelectionCoverage();
            }, 300);
          }
        }
      };

      // Add document-level event listeners
      document.addEventListener('mousemove', handleGlobalMouseMove, { passive: true });
      document.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        // Clean up
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
        }

        // Make sure to remove the style element on cleanup
        const existingStyle = document.getElementById('photo-editor-drag-style');
        if (existingStyle && existingStyle.parentNode) {
          existingStyle.parentNode.removeChild(existingStyle);
        }

        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }, [isDragging, isResizing, isRotating, overlayImage, position, scale, rotation, onTransformChange]);

    // Reset editor
    const handleReset = () => {
      setFinalImage(null);
      setProductFillImage(null);
      setSelected(true);
    };

    // Download the final image
    const handleDownload = () => {
      if (!baseImage || !canvasRef.current) return;

      const canvas = canvasRef.current;
      const link = document.createElement("a");
      link.download = "product-mask.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    };

    // Function to update dimensions when container or image changes
    const updateDimensions = useCallback(() => {
      if (!containerRef.current || !baseImage) return;
      
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      
      setContainerDimensions({
        width: containerRect.width,
        height: containerRect.height
      });
      
      // Calculate scale between original image and container
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      
      const imageWidth = baseImage.width;
      const imageHeight = baseImage.height;
      
      // Calculate how the image is scaled to fit in the container
      const scaleX = containerWidth / imageWidth;
      const scaleY = containerHeight / imageHeight;
      
      // Use the smaller scale to ensure the image fits entirely (contain)
      const scale = Math.min(scaleX, scaleY);
      
      setCanvasScale({ 
        x: scale,
        y: scale
      });
      
      // Calculate the position of the image within the container (centered)
      const scaledWidth = imageWidth * scale;
      const scaledHeight = imageHeight * scale;
      
      const left = (containerWidth - scaledWidth) / 2;
      const top = (containerHeight - scaledHeight) / 2;
      
      setCanvasPosition({
        x: left,
        y: top
      });
    }, [baseImage]);
    
    // Update dimensions when container or base image changes
    useEffect(() => {
      updateDimensions();

      // Set up resize observer to detect container size changes
      const observer = new ResizeObserver(() => {
        updateDimensions();
      });

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => {
        observer.disconnect();
      };
    }, [baseImage, updateDimensions]);

    // Set up background color to match the rest of the page
    useEffect(() => {
      // Check if the document has a dark mode class
      const isDarkMode = document.documentElement.classList.contains('dark');

      // Set the background color CSS variable based on dark/light mode
      // These values match those in GenerateVideoPage.tsx
      document.documentElement.style.setProperty(
        '--background-color',
        isDarkMode ? '#111827' : '#f9fafb' // dark:bg-gray-900 : bg-gray-50
      );

      // Update on theme change
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            const isDark = document.documentElement.classList.contains('dark');
            document.documentElement.style.setProperty(
              '--background-color',
              isDark ? '#111827' : '#f9fafb'
            );
          }
        });
      });

      observer.observe(document.documentElement, { attributes: true });

      return () => {
        observer.disconnect();
      };
    }, []);
    
    // Function to convert overlay DOM position to canvas position
    const convertPositionToCanvas = (domX: number, domY: number) => {
      if (!canvasScale.x || !canvasPosition.x) return { x: 0, y: 0 };
      
      // Convert DOM position to canvas coordinate
      const canvasX = (domX - canvasPosition.x) / canvasScale.x;
      const canvasY = (domY - canvasPosition.y) / canvasScale.y;
      
      return { x: canvasX, y: canvasY };
    };
    
    // Function to convert canvas position to overlay DOM position
    const convertCanvasToPosition = (canvasX: number, canvasY: number) => {
      // Convert canvas coordinate to DOM position
      const domX = canvasX * canvasScale.x + canvasPosition.x;
      const domY = canvasY * canvasScale.y + canvasPosition.y;
      
      return { x: domX, y: domY };
    };

    return (
      <div className="flex flex-col items-center p-4 min-h-screen transition-colors duration-200 select-none">
        <div
          ref={containerRef}
          className="relative rounded-lg my-4 max-w-full"
          style={{
            height: "calc(80vh - 120px)",
            minHeight: "400px",
            maxHeight: "800px",
            width: "100%",
            maxWidth: "1200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            backgroundColor: "var(--background-color, white)",
            border: "1px solid #e5e7eb",
            overflow: "hidden", // Clip base canvas
          }}
        >
          {/* Base canvas for rendering the background image */}
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              zIndex: 1,
              pointerEvents: "none", // Disable pointer events on canvas
              display: finalImage ? "block" : "none" // Only display canvas for final image
            }}
          />

          {/* Base image - show when not using final image */}
          {baseImage && !finalImage && (
            <img
              src={baseImageUrl}
              alt="Base"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                zIndex: 1
              }}
            />
          )}
          
          {/* Interactive overlay for product positioning - absolute positioned */}
          {baseImage && overlayImage && !finalImage && (
            <div
              className="absolute inset-0"
              style={{
                zIndex: 10,
                pointerEvents: "auto",
                overflow: "visible",
                // Hide old canvas rendering
                display: "block"
              }}
            >
              {/* Overlay product image with transform controls */}
              <div
                className="absolute"
                style={{
                  left: `${canvasPosition.x + position.x * canvasScale.x}px`,
                  top: `${canvasPosition.y + position.y * canvasScale.y}px`,
                  width: `${overlayImage.width * scale.x * canvasScale.x}px`,
                  height: `${overlayImage.height * scale.y * canvasScale.y}px`,
                  transform: `rotate(${rotation}deg)`,
                  transformOrigin: "center center",
                  border: selected ? "2px solid #8B5CF6" : "none",
                  cursor: isDragging ? "grabbing" : "grab",
                  pointerEvents: "auto",
                  zIndex: 20 // Ensure overlay is above other elements
                }}
                onMouseDown={(e) => {
                  if (!overlayImage || finalImage) return;

                  // Calculate canvas-relative position
                  const rect = containerRef.current?.getBoundingClientRect();
                  if (!rect) return;

                  const mouseX = e.clientX - rect.left;
                  const mouseY = e.clientY - rect.top;

                  // Check if click is inside the overlay image
                  const overlayRect = e.currentTarget.getBoundingClientRect();
                  const isInside =
                    mouseX >= overlayRect.left - rect.left &&
                    mouseX <= overlayRect.right - rect.left &&
                    mouseY >= overlayRect.top - rect.top &&
                    mouseY <= overlayRect.bottom - rect.top;

                  if (isInside) {
                    e.stopPropagation(); // Prevent event from bubbling
                    setIsDragging(true);
                    setSelected(true);

                    // Store starting position and current overlay position
                    const canvasCoords = convertPositionToCanvas(mouseX, mouseY);
                    setStartPosition({
                      x: canvasCoords.x,
                      y: canvasCoords.y,
                      initialPosition: { ...position }
                    });

                    // Update drag state ref
                    dragStateRef.current = {
                      isDragging: true,
                      startX: canvasCoords.x,
                      startY: canvasCoords.y,
                      initialPositionX: position.x,
                      initialPositionY: position.y
                    };
                  }
                }}
              >
                {/* Product image */}
                <img 
                  src={overlayImageUrl} 
                  alt="Product"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    pointerEvents: 'none', // Prevent image from receiving events
                  }}
                  onLoad={() => {
                    // Force a dimension update when the overlay image loads
                    updateDimensions();
                  }}
                />
                
                {/* Render transform handles if selected */}
                {selected && (
                  <>
                    {/* Corner handles for resize */}
                    <div className="absolute -top-2.5 -left-2.5 w-5 h-5 bg-purple-500 rounded-full border-2 border-white cursor-nwse-resize z-30"
                         style={{ zIndex: 30 }} // Ensure handles are above all other elements
                         onMouseDown={(e) => {
                           e.stopPropagation();
                           e.preventDefault(); // Prevent default behavior

                           // Get corner handle references
                           const handles = getTransformHandles();
                           const center = handles.center;
                           if (!center) return;

                           // Store starting dimensions without setting resize mode right away
                           const rect = containerRef.current?.getBoundingClientRect();
                           if (!rect) return;

                           const mouseX = e.clientX - rect.left;
                           const mouseY = e.clientY - rect.top;

                           // Get center point for resize calculations
                           const overlayRect = e.currentTarget.parentElement?.getBoundingClientRect();
                           if (!overlayRect) return;

                           const centerX = (overlayRect.left + overlayRect.right) / 2 - rect.left;
                           const centerY = (overlayRect.top + overlayRect.bottom) / 2 - rect.top;
                           const centerCanvas = convertPositionToCanvas(centerX, centerY);

                           const canvasCoords = convertPositionToCanvas(mouseX, mouseY);

                           // Calculate and store the initial state
                           setStartPosition({
                             x: canvasCoords.x,
                             y: canvasCoords.y,
                             initialScale: { ...scale },
                             initialPosition: { ...position },
                             width: overlayImage.width * scale.x,
                             height: overlayImage.height * scale.y,
                             initialRotation: rotation,
                             centerX: centerCanvas.x,
                             centerY: centerCanvas.y,
                           });

                           // Create a ref to track mouse movement
                           const moveTracker = { moved: false };

                           // Use a mousemove listener to detect when actual movement begins
                           const detectFirstMove = (moveEvent) => {
                             // Only start resize operation after the mouse has moved a minimum distance
                             const minDragDistance = 3; // pixels
                             const deltaX = moveEvent.clientX - e.clientX;
                             const deltaY = moveEvent.clientY - e.clientY;
                             const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

                             if (distance >= minDragDistance) {
                               moveTracker.moved = true;
                               // Now we actually start the resize operation
                               setIsResizing(true);
                               setResizeHandle("topLeft");
                               // Remove this listener since we've detected movement
                               document.removeEventListener('mousemove', detectFirstMove);
                             }
                           };

                           // Add the movement detection listener
                           document.addEventListener('mousemove', detectFirstMove);

                           // Clean up on mouse up
                           const handleMouseUp = () => {
                             document.removeEventListener('mousemove', detectFirstMove);
                             // Only if no movement occurred, make sure resize state is cleared
                             if (!moveTracker.moved) {
                               setIsResizing(false);
                             }
                           };

                           document.addEventListener('mouseup', handleMouseUp, { once: true });
                         }}
                    />
                    <div className="absolute -top-2.5 -right-2.5 w-5 h-5 bg-purple-500 rounded-full border-2 border-white cursor-nesw-resize z-30"
                         style={{ zIndex: 30 }} // Ensure handles are above all other elements
                         onMouseDown={(e) => {
                           e.stopPropagation();
                           e.preventDefault(); // Prevent default behavior

                           // Get corner handle references
                           const handles = getTransformHandles();
                           const center = handles.center;
                           if (!center) return;

                           // Store starting dimensions without setting resize mode right away
                           const rect = containerRef.current?.getBoundingClientRect();
                           if (!rect) return;

                           const mouseX = e.clientX - rect.left;
                           const mouseY = e.clientY - rect.top;

                           // Get center point for resize calculations
                           const overlayRect = e.currentTarget.parentElement?.getBoundingClientRect();
                           if (!overlayRect) return;

                           const centerX = (overlayRect.left + overlayRect.right) / 2 - rect.left;
                           const centerY = (overlayRect.top + overlayRect.bottom) / 2 - rect.top;
                           const centerCanvas = convertPositionToCanvas(centerX, centerY);

                           const canvasCoords = convertPositionToCanvas(mouseX, mouseY);

                           // Calculate and store the initial state
                           setStartPosition({
                             x: canvasCoords.x,
                             y: canvasCoords.y,
                             initialScale: { ...scale },
                             initialPosition: { ...position },
                             width: overlayImage.width * scale.x,
                             height: overlayImage.height * scale.y,
                             initialRotation: rotation,
                             centerX: centerCanvas.x,
                             centerY: centerCanvas.y,
                           });

                           // Create a ref to track mouse movement
                           const moveTracker = { moved: false };

                           // Use a mousemove listener to detect when actual movement begins
                           const detectFirstMove = (moveEvent) => {
                             // Only start resize operation after the mouse has moved a minimum distance
                             const minDragDistance = 3; // pixels
                             const deltaX = moveEvent.clientX - e.clientX;
                             const deltaY = moveEvent.clientY - e.clientY;
                             const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

                             if (distance >= minDragDistance) {
                               moveTracker.moved = true;
                               // Now we actually start the resize operation
                               setIsResizing(true);
                               setResizeHandle("topRight");
                               // Remove this listener since we've detected movement
                               document.removeEventListener('mousemove', detectFirstMove);
                             }
                           };

                           // Add the movement detection listener
                           document.addEventListener('mousemove', detectFirstMove);

                           // Clean up on mouse up
                           const handleMouseUp = () => {
                             document.removeEventListener('mousemove', detectFirstMove);
                             // Only if no movement occurred, make sure resize state is cleared
                             if (!moveTracker.moved) {
                               setIsResizing(false);
                             }
                           };

                           document.addEventListener('mouseup', handleMouseUp, { once: true });
                         }}
                    />
                    <div className="absolute -bottom-2.5 -left-2.5 w-5 h-5 bg-purple-500 rounded-full border-2 border-white cursor-nesw-resize z-30"
                         style={{ zIndex: 30 }} // Ensure handles are above all other elements
                         onMouseDown={(e) => {
                           e.stopPropagation();
                           e.preventDefault(); // Prevent default behavior

                           // Get corner handle references
                           const handles = getTransformHandles();
                           const center = handles.center;
                           if (!center) return;

                           // Store starting dimensions without setting resize mode right away
                           const rect = containerRef.current?.getBoundingClientRect();
                           if (!rect) return;

                           const mouseX = e.clientX - rect.left;
                           const mouseY = e.clientY - rect.top;

                           // Get center point for resize calculations
                           const overlayRect = e.currentTarget.parentElement?.getBoundingClientRect();
                           if (!overlayRect) return;

                           const centerX = (overlayRect.left + overlayRect.right) / 2 - rect.left;
                           const centerY = (overlayRect.top + overlayRect.bottom) / 2 - rect.top;
                           const centerCanvas = convertPositionToCanvas(centerX, centerY);

                           const canvasCoords = convertPositionToCanvas(mouseX, mouseY);

                           // Calculate and store the initial state
                           setStartPosition({
                             x: canvasCoords.x,
                             y: canvasCoords.y,
                             initialScale: { ...scale },
                             initialPosition: { ...position },
                             width: overlayImage.width * scale.x,
                             height: overlayImage.height * scale.y,
                             initialRotation: rotation,
                             centerX: centerCanvas.x,
                             centerY: centerCanvas.y,
                           });

                           // Create a ref to track mouse movement
                           const moveTracker = { moved: false };

                           // Use a mousemove listener to detect when actual movement begins
                           const detectFirstMove = (moveEvent) => {
                             // Only start resize operation after the mouse has moved a minimum distance
                             const minDragDistance = 3; // pixels
                             const deltaX = moveEvent.clientX - e.clientX;
                             const deltaY = moveEvent.clientY - e.clientY;
                             const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

                             if (distance >= minDragDistance) {
                               moveTracker.moved = true;
                               // Now we actually start the resize operation
                               setIsResizing(true);
                               setResizeHandle("bottomLeft");
                               // Remove this listener since we've detected movement
                               document.removeEventListener('mousemove', detectFirstMove);
                             }
                           };

                           // Add the movement detection listener
                           document.addEventListener('mousemove', detectFirstMove);

                           // Clean up on mouse up
                           const handleMouseUp = () => {
                             document.removeEventListener('mousemove', detectFirstMove);
                             // Only if no movement occurred, make sure resize state is cleared
                             if (!moveTracker.moved) {
                               setIsResizing(false);
                             }
                           };

                           document.addEventListener('mouseup', handleMouseUp, { once: true });
                         }}
                    />
                    <div className="absolute -bottom-2.5 -right-2.5 w-5 h-5 bg-purple-500 rounded-full border-2 border-white cursor-nwse-resize z-30"
                         style={{ zIndex: 30 }} // Ensure handles are above all other elements
                         onMouseDown={(e) => {
                           e.stopPropagation();
                           e.preventDefault(); // Prevent default behavior

                           // Get corner handle references
                           const handles = getTransformHandles();
                           const center = handles.center;
                           if (!center) return;

                           // Store starting dimensions without setting resize mode right away
                           const rect = containerRef.current?.getBoundingClientRect();
                           if (!rect) return;

                           const mouseX = e.clientX - rect.left;
                           const mouseY = e.clientY - rect.top;

                           // Get center point for resize calculations
                           const overlayRect = e.currentTarget.parentElement?.getBoundingClientRect();
                           if (!overlayRect) return;

                           const centerX = (overlayRect.left + overlayRect.right) / 2 - rect.left;
                           const centerY = (overlayRect.top + overlayRect.bottom) / 2 - rect.top;
                           const centerCanvas = convertPositionToCanvas(centerX, centerY);

                           const canvasCoords = convertPositionToCanvas(mouseX, mouseY);

                           // Calculate and store the initial state
                           setStartPosition({
                             x: canvasCoords.x,
                             y: canvasCoords.y,
                             initialScale: { ...scale },
                             initialPosition: { ...position },
                             width: overlayImage.width * scale.x,
                             height: overlayImage.height * scale.y,
                             initialRotation: rotation,
                             centerX: centerCanvas.x,
                             centerY: centerCanvas.y,
                           });

                           // Create a ref to track mouse movement
                           const moveTracker = { moved: false };

                           // Use a mousemove listener to detect when actual movement begins
                           const detectFirstMove = (moveEvent) => {
                             // Only start resize operation after the mouse has moved a minimum distance
                             const minDragDistance = 3; // pixels
                             const deltaX = moveEvent.clientX - e.clientX;
                             const deltaY = moveEvent.clientY - e.clientY;
                             const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

                             if (distance >= minDragDistance) {
                               moveTracker.moved = true;
                               // Now we actually start the resize operation
                               setIsResizing(true);
                               setResizeHandle("bottomRight");
                               // Remove this listener since we've detected movement
                               document.removeEventListener('mousemove', detectFirstMove);
                             }
                           };

                           // Add the movement detection listener
                           document.addEventListener('mousemove', detectFirstMove);

                           // Clean up on mouse up
                           const handleMouseUp = () => {
                             document.removeEventListener('mousemove', detectFirstMove);
                             // Only if no movement occurred, make sure resize state is cleared
                             if (!moveTracker.moved) {
                               setIsResizing(false);
                             }
                           };

                           document.addEventListener('mouseup', handleMouseUp, { once: true });
                         }}
                    />
                    
                    {/* Rotation handle */}
                    <div
                      className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-8 w-6 h-6 flex items-center justify-center bg-purple-500 rounded-full border-2 border-white cursor-grab z-30"
                      style={{ zIndex: 40 }} // Ensure rotation handle is on top
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault(); // Prevent default behavior
                        setIsRotating(true);

                        // Get center point for rotation
                        const rect = containerRef.current?.getBoundingClientRect();
                        if (!rect) return;

                        const mouseX = e.clientX - rect.left;
                        const mouseY = e.clientY - rect.top;

                        // Calculate center of the overlay image
                        const overlayRect = e.currentTarget.parentElement?.getBoundingClientRect();
                        if (!overlayRect) return;

                        const centerX = (overlayRect.left + overlayRect.right) / 2 - rect.left;
                        const centerY = (overlayRect.top + overlayRect.bottom) / 2 - rect.top;

                        // Store start position for rotation
                        const canvasCoords = convertPositionToCanvas(mouseX, mouseY);
                        const centerCanvas = convertPositionToCanvas(centerX, centerY);

                        setStartPosition({
                          x: canvasCoords.x,
                          y: canvasCoords.y,
                          initialRotation: rotation,
                          centerX: centerCanvas.x,
                          centerY: centerCanvas.y,
                        });
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>🔄</span>
          </div>
                    
                    {/* Display rotation value */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/80 px-2 py-1 rounded text-xs font-medium text-purple-600">
                      {Math.round(rotation)}°
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {finalImage && (
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded bg-purple-500 dark:bg-purple-600 text-white hover:bg-purple-600 dark:hover:bg-purple-700 transition-colors duration-200"
            >
              Reset
            </button>

            <button
              onClick={handleDownload}
              className="px-4 py-2 rounded bg-blue-500 dark:bg-blue-600 text-white hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors duration-200"
            >
              Download
            </button>
          </div>
        )}
      </div>
    );
  }
);

export default PhotoEditor;
